import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CategoryFilter from '../../components/ui/CategoryFilter';
import CartItem from './components/CartItem';
import OrderSummary from './components/OrderSummary';
import EmptyCart from './components/EmptyCart';
import CheckoutModal from './components/CheckoutModal';
import SuccessModal from './components/SuccessModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  const mockCartData = [
  {
    id: 1,
    title: "Strategic Planning Framework: From Vision to Execution",
    author: "Dr. Michael Chen",
    price: 49.99,
    quantity: 1,
    format: "pdf",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6d01ec7-1764730952506.png",
    imageAlt: "Professional business strategy book cover with blue and white geometric design showing organizational charts and planning frameworks"
  },
  {
    id: 2,
    title: "Digital Transformation Playbook: A Step-by-Step Guide",
    author: "Sarah Williams",
    price: 59.99,
    quantity: 1,
    format: "pdf",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb43077f-1764663757339.png",
    imageAlt: "Modern technology-themed book cover featuring digital network connections and transformation symbols in vibrant blue tones"
  }];


  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setCartItems(parsedCart);
      } catch (error) {
        console.error('Error parsing cart data:', error);
        setCartItems([]);
      }
    } else {
      setCartItems(mockCartData);
      localStorage.setItem('cart', JSON.stringify(mockCartData));
    }
  }, []);

  const updateCart = (newCart) => {
    setCartItems(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const handleUpdateQuantity = (itemId, newQuantity) => {
    const updatedCart = cartItems?.map((item) =>
    item?.id === itemId ? { ...item, quantity: newQuantity } : item
    );
    updateCart(updatedCart);
  };

  const handleFormatChange = (itemId, newFormat) => {
    const updatedCart = cartItems?.map((item) =>
    item?.id === itemId ? { ...item, format: newFormat } : item
    );
    updateCart(updatedCart);
  };

  const handleRemoveItem = (itemId) => {
    const updatedCart = cartItems?.filter((item) => item?.id !== itemId);
    updateCart(updatedCart);
  };

  const handleApplyPromo = () => {
    const validPromoCodes = {
      'SAVE10': 0.10,
      'WELCOME20': 0.20,
      'STRATEGY15': 0.15
    };

    const upperPromo = promoCode?.toUpperCase();
    if (validPromoCodes?.[upperPromo]) {
      setAppliedDiscount(validPromoCodes?.[upperPromo]);
      setPromoError('');
    } else {
      setPromoError('Invalid promo code');
      setAppliedDiscount(0);
    }
  };

  const calculateSubtotal = () => {
    return cartItems?.reduce((sum, item) => sum + item?.price * item?.quantity, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = subtotal * appliedDiscount;
    return subtotal - discount;
  };

  const handleCheckout = () => {
    setIsCheckoutOpen(true);
  };

  const handleCheckoutComplete = () => {
    const orderNumber = `ORD-${Date.now()?.toString()?.slice(-8)}`;
    setOrderDetails({
      orderNumber,
      total: calculateTotal(),
      itemCount: cartItems?.length,
      email: 'user@example.com'
    });
    setIsCheckoutOpen(false);
    setIsSuccessOpen(true);
    updateCart([]);
    setAppliedDiscount(0);
    setPromoCode('');
  };

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
  };

  const subtotal = calculateSubtotal();
  const discount = subtotal * appliedDiscount;
  const total = calculateTotal();

  return (
    <>
      <Helmet>
        <title>Shopping Cart - StrategyShelf</title>
        <meta name="description" content="Review your selected business strategy eBooks and complete your secure purchase with instant download access." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <CategoryFilter />

        <main className="pt-32 pb-12 md:pb-16 lg:pb-20">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="mb-6 md:mb-8">
              <button
                onClick={() => navigate('/e-book-catalog')}
                className="flex items-center gap-2 text-sm md:text-base text-muted-foreground hover:text-foreground transition-smooth focus-ring rounded-md">
                
                <Icon name="ArrowLeft" size={20} />
                <span>Continue Shopping</span>
              </button>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-6 md:mb-8">
              Shopping Cart
            </h1>

            {cartItems?.length === 0 ?
            <EmptyCart /> :

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="lg:col-span-2 space-y-4 md:space-y-6">
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
                        Cart Items ({cartItems?.length})
                      </h2>
                      {cartItems?.length > 0 &&
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={() => updateCart([])}
                      className="text-error hover:text-error hover:bg-error/10">
                      
                          Clear Cart
                        </Button>
                    }
                    </div>
                  </div>

                  <div className="space-y-4">
                    {cartItems?.map((item) =>
                  <CartItem
                    key={item?.id}
                    item={item}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemove={handleRemoveItem}
                    onFormatChange={handleFormatChange} />

                  )}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <OrderSummary
                  subtotal={subtotal}
                  discount={discount}
                  total={total}
                  promoCode={promoCode}
                  onPromoCodeChange={setPromoCode}
                  onApplyPromo={handleApplyPromo}
                  promoError={promoError}
                  onCheckout={handleCheckout}
                  itemCount={cartItems?.length} />
                
                </div>
              </div>
            }

            {cartItems?.length > 0 &&
            <div className="mt-8 md:mt-12 bg-card rounded-lg border border-border p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-4 md:mb-6">
                  Why Choose Us?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <Icon name="Shield" size={20} className="text-success" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">Secure Payment</h3>
                      <p className="text-sm text-muted-foreground">
                        SSL encrypted checkout with multiple payment options
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <Icon name="Download" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">Instant Access</h3>
                      <p className="text-sm text-muted-foreground">
                        Download your eBooks immediately after purchase
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-md flex items-center justify-center flex-shrink-0">
                      <Icon name="RefreshCw" size={20} className="text-accent" />
                    </div>
                    <div>
                      <h3 className="font-medium text-foreground mb-1">Money-Back Guarantee</h3>
                      <p className="text-sm text-muted-foreground">
                        30-day refund policy if you're not satisfied
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </main>

        <CheckoutModal
          isOpen={isCheckoutOpen}
          onClose={() => setIsCheckoutOpen(false)}
          total={total}
          onComplete={handleCheckoutComplete} />
        

        {orderDetails &&
        <SuccessModal
          isOpen={isSuccessOpen}
          onClose={handleSuccessClose}
          orderDetails={orderDetails} />

        }
        
        <footer className="bg-card border-t border-border py-8 px-4 md:px-6">
          <div className="max-w-[1536px] mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <img
                  src="/assets/images/Logo_final_no_background-1771860991795-1772040132991.png"
                  alt="StrategyShelf Logo"
                  className="h-8 w-auto object-contain"
                />
              </div>
              
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <span>📞 346-523-0048</span>
                </div>
                <div className="flex items-center gap-2">
                  <span>✉️ info@thestrategyshelf.com</span>
                </div>
                <button className="hover:text-foreground transition-smooth">Privacy Policy</button>
                <button className="hover:text-foreground transition-smooth">Terms of Service</button>
                <button className="hover:text-foreground transition-smooth">Contact</button>
              </div>
              
              <p className="text-sm text-muted-foreground">
                &copy; {new Date()?.getFullYear()} StrategyShelf. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );

};

export default ShoppingCart;